import { t as translateDate } from './translate-date.js';
import { g as getBookingWidgetDataFromObject } from './_234.js';
import './index2.js';

/**
 * **Bedclouds v3**
 */
function v3Factory({ window, document, thn }) {
    const engine = {};
    let onCheckoutSuccessEvent = null;
    engine.onStateChange = thn.debounce(thn.pageCheck, 500);
    engine.boot = function boot() {
        function observeLanguage() {
            thn.languageCheck();
        }
        const langContainer = document.querySelector('html');
        if (langContainer) {
            thn.observeDom(langContainer, observeLanguage, {
                childList: false,
                subtree: false,
                attributes: true,
            });
            thn.shutdown(() => {
                thn.stopObserveDom(langContainer, observeLanguage);
            });
        }
    };
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const container = document.querySelector('body');
        if (container) {
            const refreshPrice = thn.debounce(() => {
                const loader = document.querySelector('.chakra-skeleton');
                if (!loader) {
                    ctx.widget.refresh();
                }
            }, 500);
            thn.observeDom(container, refreshPrice, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
            });
            thn.pageUnload(() => {
                refreshPrice.cancel();
                thn.stopObserveDom(container, refreshPrice);
            });
        }
    };
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        // Dates
        const dateBegin = thn.parseUrlParameter('checkin')?.replace(/-/g, '/');
        const dateEnd = thn.parseUrlParameter('checkout')?.replace(/-/g, '/');
        let nights = 0;
        if (dateBegin && dateEnd) {
            widgetData.dateBegin = dateBegin;
            widgetData.dateEnd = dateEnd;
            nights = thn.daysBetween(dateBegin, dateEnd);
        }
        // Price & Currency || No Rooms
        const roomCards = document.querySelectorAll('.cb-accommodation-card');
        function getMinimunPriceAndMaxGuests(roomCard) {
            const pricesEl = roomCard.querySelectorAll('.cb-rate-plan .cb-rate-plan-price, .cb-rate-plan>div>div>div:first-child>div:nth-child(2)>div>p.chakra-text.cb-text');
            const prices = [...pricesEl].map((price) => thn.parseNumber(price?.textContent)).filter(Boolean);
            const minPrice = Math.min(...prices);
            const currency = thn.parseCurrency(thn.parseUrlParameter('currency')) || thn.parseCurrency(pricesEl?.[0]?.textContent);
            const guestEl = roomCard.querySelector('[data-testid^="accommodation-card"]');
            const guests = thn.parseNumber(guestEl?.textContent) || 1; // In case of hostels we put 1 guest by default
            return { minPrice, currency, guests };
        }
        widgetData.noRooms = false;
        const isPricePerNightUsers = [1045106, 1046915, 1034364].includes(thn.data.accountUserId);
        const isPricePerNightProperties = [1015770].includes(thn.data.propertyId);
        const isPricePerNight = isPricePerNightProperties || isPricePerNightUsers;
        roomCards.forEach((room) => {
            const { minPrice, currency, guests } = getMinimunPriceAndMaxGuests(room);
            if (!widgetData.price || minPrice < widgetData.price) {
                widgetData.price = isPricePerNight ? minPrice : minPrice / nights;
                widgetData.currency = currency;
                widgetData.adults = guests;
                widgetData.children = 0;
                widgetData.set = true;
            }
        });
        return widgetData;
    };
    /**
       * @memberOf module:bs_234
       * @this thn.widgets.booking
       * @see {@link Booking#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingObserveDom = function bookingObserveDom(ctx) {
        if (onCheckoutSuccessEvent) {
            // Already listening, skip ...
            return;
        }
        if (this.isSet()) {
            // Data was already set, skip ...
            return;
        }
        onCheckoutSuccessEvent = () => {
            if (this.setData().isSet()) {
                this.save();
                thn.bus.detach('dataLayer:push', onCheckoutSuccessEvent);
            }
        };
        thn.bus.on('dataLayer:push', onCheckoutSuccessEvent, true);
        thn.pageUnload(() => {
            thn.bus.detach('dataLayer:push', onCheckoutSuccessEvent);
        });
    };
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        const dataLayer = window.dataLayer;
        const bookingDataPurchase = dataLayer.find((item) => item[0] === 'event' && item[1] === 'purchase');
        const bookingDataConversion = dataLayer.find((item) => item[0] === 'event' && item[1] === 'conversion');
        const bookingId = document.querySelector('.cb-confirmation-page p')?.textContent?.split('#')[1] || thn.objectGet(bookingDataPurchase, 'transaction_id', { first: false });
        if (!bookingId) {
            widgetData.set = false;
            return widgetData;
        }
        // Booking ID
        widgetData.bookingId = bookingId;
        // Dates
        const datesRaw = document.querySelectorAll('.check-box p:nth-child(2)');
        const dateBeginFromRaw = !thn.empty(datesRaw) && translateDate(datesRaw[0]?.textContent, thn.data.language);
        const dateEndFromRaw = !thn.empty(datesRaw) && translateDate(datesRaw[1]?.textContent, thn.data.language);
        widgetData.dateBegin = bookingDataConversion?.[2]?.items[0]?.start_date || thn.parseUrlParameter('checkin')?.replace(/-/g, '/') || dateBeginFromRaw;
        widgetData.dateEnd = bookingDataConversion?.[2]?.items[0]?.end_date || thn.parseUrlParameter('checkout')?.replace(/-/g, '/') || dateEndFromRaw;
        // Adults & Children & Guests
        widgetData.adults = bookingDataPurchase?.[2]?.items[0]?.adults;
        widgetData.children = bookingDataPurchase?.[2]?.items[0]?.kids;
        widgetData.guests = widgetData.adults + widgetData.children;
        // Price & Currency || Rooms
        widgetData.price = thn.parseNumber(thn.objectGet(bookingDataPurchase, 'value', { first: false }));
        widgetData.currency = thn.parseCurrency(thn.objectGet(bookingDataPurchase, 'currency', { first: false }));
        return widgetData;
    };
    return engine;
}

/**
 * **Bedclouds**
 */
function v1v2Factory({ window, document, thn }) {
    const engine = {};
    const isBookingEngine = window.location.hostname.match(/cloudbeds.com/);
    const isDesignPraqueHotel = thn.data.accountUserId === 1044120;
    engine._isLoading = function () {
        const loadingEl = document.querySelector('.spinner_container');
        return !!(loadingEl && thn.isVisible(loadingEl));
    };
    engine.languageCheck = function languageCheck(ctx) {
        return new Promise(function (resolve, reject) {
            let language;
            if (thn.data.propertyId === 1031257 || thn.data.propertyId === 1031258) {
                resolve('es');
                return;
            }
            if (thn.data.propertyId === 1024950) {
                const queryText = (selector) => document.querySelector(selector)?.textContent || '';
                language = queryText('.header-menu #dropdownBtn');
                if (language === 'Español') {
                    language = 'es';
                }
                else if (language === 'Portugues') {
                    language = 'pt';
                }
                else if (language === 'English') {
                    language = 'en';
                }
                if (language && ctx.langs.test(language)) {
                    resolve(language);
                    return;
                }
            }
            const langEl = document.querySelector('#lang-selector .selected');
            const lang = (window.CBBooking && window.CBBooking.current_lang)
                || (langEl && langEl.getAttribute('data-lang')); // Obtain the lang code from the DOM as a fallback
            language = lang && lang.substr(0, 2);
            if (language === 'ua') {
                language = 'uk';
            }
            else if (language === 'cn') {
                language = 'zh';
            }
            else if (language === 'vn') {
                language = 'vi';
            }
            if (isDesignPraqueHotel) {
                language = isBookingEngine ? language : document.documentElement.lang;
                if (language && ctx.langs.test(language)) {
                    resolve(language);
                    return;
                }
                resolve('en');
                return;
            }
            if (window.location.hostname.indexOf('novo.elmistihostels') >= 0) {
                language = (window.location.pathname.match(ctx.urlRe) || [])[1];
                if (language && ctx.langs.test(language)) {
                    resolve(language);
                    return;
                }
                resolve('en');
                return;
            }
            if (window.location.hostname.indexOf('sweetbcnapartments') >= 0) {
                resolve(ctx.getHtmlLang() || 'es');
                return;
            }
            if (language && ctx.langs.test(language)) {
                resolve(language);
                return;
            }
            reject('no_language');
        });
    };
    engine.generateSearchUrl = function generateSearchUrl(ctx) {
        const baseUrl = new URL(ctx.baseUrl);
        const urlHash = new URLSearchParams(thn.trim(baseUrl.hash, '#'));
        if (!baseUrl.host.match(/hotels.cloudbeds/)) {
            ctx.newUrl = '';
            return ctx;
        }
        urlHash.set('promo', '');
        urlHash.delete('ccode');
        urlHash.delete('unk');
        urlHash.set('checkin', '[date_begin]');
        urlHash.set('checkout', '[date_end]');
        const formatDate = (value) => value.replace(/\//g, '-');
        if (ctx.data.dateBegin) {
            ctx.newData.dateBegin = formatDate(ctx.data.dateBegin);
        }
        if (ctx.data.dateEnd) {
            ctx.newData.dateEnd = formatDate(ctx.data.dateEnd);
        }
        baseUrl.hash = urlHash.toString();
        ctx.newUrl = baseUrl.toString();
        return ctx;
    };
    engine.boot = function boot() {
        // Sometimes we don't have enough time to register the booking
        // in the "Booking Confirmed" page, therefore we are going to
        // store the raw data object in sessionStorage ASAP, so we have
        // the chance to parse that object later in the "Thank you" page
        // (Sample thank you page: https://mkt.elmistihostels.com/obrigado-reserva-pt/)
        //
        // Additionally the "thank you" could contain a corpo script,
        // in that case, we must add a "booking_parse_data" function in the
        // corresponding corpo engine (example: src/engine/1040126.mjs),
        // AND map the "thank you page" as a "Booking Confirmed" corpo page
        // in order to trigger the execution of "booking_parse_data".
        if (window.createdBooking) {
            try {
                thn.logger.debug('Storing "createdBooking" object');
                thn.container.globalThis.sessionStorage.set('thn_booking', JSON.stringify({
                    propertyId: thn.data.propertyId,
                    data: window.createdBooking,
                }));
            }
            catch (e) {
                thn.logger.debug('Error Storing "createdBooking" Object');
            }
        }
        // Detect page changes caused by the breadcrumb
        if (!thn.bedcloudsBreadcrumbObserver) {
            const breadcrumbEls = Array.from(document.querySelectorAll('.page-breadcrumb li'));
            if (breadcrumbEls.length) {
                // Initial state
                thn.data.bedcloudsBreadcrumbActiveItem = document.querySelector('.page-breadcrumb li.active');
                thn.bedcloudsBreadcrumbObserver = thn.debounce(function thnBedcloudsBreadcrumbObserver() {
                    const breadcrumbActiveItem = document.querySelector('.page-breadcrumb li.active');
                    // The active item changed
                    if (thn.data.bedcloudsBreadcrumbActiveItem !== breadcrumbActiveItem) {
                        thn.pageCheck({ force: true });
                        // If we are in R&R, refresh widget
                        if (['Rooms and Rates', 'Packages'].includes(thn.data.pageName) && thn.widgets.price.refresh() && !thn.widgets.price.isSet()) {
                            thn.widgets.price.hide();
                        }
                        thn.data.bedcloudsBreadcrumbActiveItem = breadcrumbActiveItem;
                    }
                }, 500);
                const breadcrumbObserverConfig = {
                    childList: true,
                    subtree: true,
                    characterData: false,
                    attributes: true,
                    attributeFilter: ['class'],
                };
                // Observe every breadcrumb item
                breadcrumbEls.forEach(function (el) {
                    thn.observeDom(el, thn.bedcloudsBreadcrumbObserver, breadcrumbObserverConfig);
                    thn.shutdown(function () {
                        thn.stopObserveDom(el, thn.bedcloudsBreadcrumbObserver);
                    });
                });
                thn.shutdown(function () {
                    delete thn.bedcloudsBreadcrumbObserver;
                    delete thn.data.bedcloudsBreadcrumbActiveItem;
                });
            }
        }
        // Keep track of the promocode values
        const undelegate = thn.delegate('click', '.find-by-promo, #promo_remove', function () {
            thn.data.bedcloudsPreviousPromocode = thn.data.bedcloudsPromocode || '';
            thn.data.bedcloudsPromocode = thn.widgets.price.getPromocode().promocode;
        });
        thn.shutdown(function () {
            delete thn.data.bedcloudsPromocode;
            delete thn.data.bedcloudsPreviousPromocode;
            undelegate();
        });
        if (isDesignPraqueHotel && !isBookingEngine) {
            const htmlElement = document.documentElement;
            const langReloader = thn.debounce(thn.languageCheck, 400);
            thn.observeDom(htmlElement, langReloader, {
                childList: false,
                subtree: false,
                attributes: true,
                attributeFilter: ['lang'],
            });
            thn.shutdown(() => {
                langReloader.cancel();
                thn.stopObserveDom(htmlElement, langReloader);
            });
        }
        // check if we are in the booking iframe
        const iframe = document.querySelector('.wixui-section.undefined, [class*=BookingModal]');
        if (iframe) {
            let refreshPage = thn.debounce(thn.pageCheck, 800);
            thn.observeDom(iframe, refreshPage, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.shutdown(() => {
                refreshPage.cancel();
                thn.stopObserveDom(iframe, refreshPage);
                refreshPage = null;
            });
        }
    };
    engine.onStateChange = function onStateChange(ctx) {
        if (!['Rooms and Rates', 'Packages'].includes(thn.data.pageName)) {
            thn.logger.debug('---   State changed, triggering page_check   ---');
            setTimeout(thn.pageCheck, 2000);
        }
    };
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const loadingEl = document.querySelector && document.querySelector('.spinner_container');
        if (loadingEl && !thn.bedcloudsRnrObserver) {
            thn.bedcloudsRnrObserver = thn.debounce(function thnBedcloudsRnrObserver() {
                // If we are still in R&R after loading took place
                if (!engine._isLoading()) {
                    if (thn.data.bedcloudsPreviousPromocode !== thn.data.bedcloudsPromocode) {
                        // If promocode has changed, we need a "page reload"
                        thn.logger.debug('Promocode changed, perform reload');
                        thn.logger.debug('Previous: ', thn.data.bedcloudsPreviousPromocode);
                        thn.logger.debug('Current: ', thn.data.bedcloudsPromocode);
                        // Reset the previous know promocode
                        thn.data.bedcloudsPreviousPromocode = thn.data.bedcloudsPromocode;
                        // Full reload
                        thn.widgets.price.reset({ onlyData: true });
                        thn.widgets.package.reset({ onlyData: true });
                        thn.pageCheck();
                        if (thn.data.pageName === 'Rooms and Rates') {
                            thn.widgets.price.update();
                        }
                    }
                    else {
                        thn.widgets.price.reset();
                        thn.widgets.package.reset();
                        thn.pageCheck({ force: true });
                    }
                }
            }, 1800);
            thn.observeDom(loadingEl, thn.bedcloudsRnrObserver, {
                childList: false,
                subtree: false,
                characterData: false,
                attributes: true,
                attributeFilter: ['style'],
            });
            thn.pageUnload(function () {
                thn.stopObserveDom(loadingEl, thn.bedcloudsRnrObserver);
                delete thn.bedcloudsRnrObserver;
            });
        }
        const bookingContainer = document.querySelector('.book-content-wrapper') || document.querySelector('#wizard-container');
        if (bookingContainer && !thn.bedcloudsRnrObserver) {
            thn.bedcloudsRnrObserver = thn.debounce(() => {
                ctx.widget.update();
            }, 900);
            thn.observeDom(bookingContainer, thn.bedcloudsRnrObserver, {
                subtree: true,
            });
            thn.pageUnload(function () {
                thn.stopObserveDom(bookingContainer, thn.bedcloudsRnrObserver);
                delete thn.bedcloudsRnrObserver;
            });
        }
    };
    engine.priceParseData_v1 = function priceParseData_v1(ctx) {
        const isUserUdayaResorts = thn.data.accountUserId === 1042548;
        const widgetData = {};
        let nights;
        const uidDefaultGuests = [1045262];
        if (engine._isLoading()) {
            this.logger.debug('Booking engine still loading...');
            return widgetData;
        }
        const dataObj = window.CBBooking;
        if (!thn.empty(dataObj)) {
            // Dates
            widgetData.dateBegin = ((dataObj && dataObj.checkin)
                || thn.parseUrlParameter('checkin')
                || '').replace(/-/g, '/');
            widgetData.dateEnd = ((dataObj && dataObj.checkout)
                || thn.parseUrlParameter('checkout')
                || '').replace(/-/g, '/');
            if (widgetData.dateBegin && widgetData.dateEnd) {
                nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
            }
            // Rooms
            widgetData.rooms = 1;
            // Guests
            widgetData.adults = 2;
            widgetData.children = 0;
            // For Pyttegarden Holiday Apartments (p1026762)
            // We need to flag as priceSpecial when we have a room filter apply and it's not Type A room type ("TA1" or "TA2")
            if (dataObj.room_type && !dataObj.room_type.match(/TA1|TA2/)) {
                widgetData.priceSpecial = 1;
            }
            // Currency
            widgetData.currency = thn.getCurrencyCode(dataObj.user_currency && dataObj.user_currency.currency_code);
            const prefersDomData = thn.data.propertyId === 1033199;
            // Price
            if (dataObj && dataObj.roomRates && !prefersDomData) {
                if (!thn.empty(window.CBBooking.roomRates)) {
                    // In some properties, "rate_basic" is the total price (all nights)
                    // and we should use "rate_min" instead
                    // In the same line, in some properties "max_guests" attribute does not work
                    // so we should collect "num_beds"
                    // that's why we filter them with occupancy_whitelist.
                    const rateMinWhitelist = [1025394].includes(thn.data.propertyId);
                    const occupancyWhitelist = [1033197, 1033200, 1029300].includes(thn.data.propertyId);
                    // Price for properties that the right price is not in the object "rate_min" nor "rate_basic" but in "room_type_total_rate_formatted"
                    const isTotalRateFormatted = [1034076, 1032070, 1029433, 1033198, 1037254, 1033200, 1033197, 1033199, 1039049, 1035220].includes(thn.data.propertyId);
                    const rates = Object.keys(dataObj.roomRates).map(function (key) {
                        const rate = dataObj.roomRates[key];
                        let price;
                        if (rateMinWhitelist) {
                            price = thn.parseNumber(rate.rate_min);
                        }
                        else if (isTotalRateFormatted) {
                            price = thn.parseNumber(rate.room_type_total_rate_formatted);
                        }
                        else {
                            price = thn.parseNumber(rate.rate_basic);
                        }
                        const roomType = rate.room_type_short_title;
                        const basicAdults = occupancyWhitelist ? (thn.parseNumber(rate.num_beds) || 2) : (thn.parseNumber(rate.max_guests) || 2); // we are collecting max_guests attribute because is the selector that matches the wanted data in most of the hotels associated to this BE.
                        if (price && roomType !== 'TEN') { // We don't take into account Tent/Van rate for property 1024483
                            return {
                                adults: basicAdults,
                                price,
                            };
                        }
                        return null;
                    }).filter((rate) => rate);
                    const minPrice = rates.length && rates.reduce(function (prev, curr) {
                        return (curr.price < prev.price) ? curr : prev;
                    });
                    if (minPrice) {
                        // Guests
                        widgetData.adults = uidDefaultGuests.includes(thn.data.accountUserId) ? 2 : minPrice.adults;
                        widgetData.children = 0;
                        // Price
                        widgetData.price = rateMinWhitelist ? minPrice.price : ((minPrice.price / nights) * 100) / 100;
                        widgetData.set = true;
                    }
                    else {
                        widgetData.noRooms = true;
                    }
                }
                else {
                    widgetData.noRooms = true;
                }
            }
            else { // DOM alternative
                const rateEls = document.querySelectorAll('.room:not([data-short-name="TEN"])'); // We don't take into account Tent/Van rate for property 1024483
                if (rateEls && rateEls.length) {
                    const rates = Array.from(rateEls).map(function (rateEl) {
                        const priceEl = rateEl.querySelector('.rate_basic_derived');
                        if (priceEl) {
                            const price = thn.parseNumber(priceEl.textContent);
                            if (price) {
                                const adultsEl = priceEl.querySelector('.custom-max-guests');
                                const adults = (adultsEl && thn.parseNumber(adultsEl.textContent)) || 2;
                                return {
                                    adults,
                                    currency: thn.getCurrencyCode((priceEl.textContent.match(/[^\d\s,.]+/) || [])[0]),
                                    price,
                                };
                            }
                        }
                        return null;
                    }).filter((rate) => rate);
                    const minPrice = rates.reduce(function (prev, curr) {
                        return (curr.price < prev.price) ? curr : prev;
                    });
                    if (minPrice) {
                        // Guests
                        widgetData.adults = uidDefaultGuests.includes(thn.data.accountUserId) ? 2 : minPrice.adults;
                        widgetData.children = 0;
                        // Currency fallback
                        if (!widgetData.currency && minPrice.currency) {
                            widgetData.currency = minPrice.currency;
                        }
                        // Price
                        widgetData.price = minPrice.price;
                        // Price per night
                        if (widgetData.price && nights) {
                            widgetData.price = Math.round((widgetData.price / nights) * 100) / 100;
                        }
                        widgetData.set = true;
                    }
                    else {
                        widgetData.noRooms = true;
                    }
                }
                else {
                    widgetData.noRooms = true;
                }
            }
            if (isUserUdayaResorts) {
                widgetData.adults = 2;
            }
            widgetData.guests = widgetData.adults + widgetData.children;
        }
        return widgetData;
    };
    /**
         *
         * @memberOf module:bs_234
         * @this thn.widgets.price
         * @see {@link Price#set_data}
         *
         * @param {object} ctx - The context object.
         */
    engine.priceParseData_v2 = function priceParseData_v2(ctx) {
        const widgetData = {};
        // When arriving at R&R are not dates selected, defect dates are today and tomorrow
        const today = thn.dateToString((new Date()).toDateString());
        const tomorrow = thn.dateToString(thn.addDays(today, 1));
        if (window.location && window.location.search) {
            widgetData.dateBegin = (thn.parseUrlParameter('start-date') || '').replace(/-/g, '/');
            widgetData.dateEnd = (thn.parseUrlParameter('end-date') || '').replace(/-/g, '/');
        }
        else {
            widgetData.dateBegin = today;
            widgetData.dateEnd = tomorrow;
        }
        // Guests
        widgetData.adults = parseInt(thn.parseUrlParameter('guests'), 10) || 2;
        widgetData.children = 0;
        widgetData.guests = widgetData.adults + widgetData.children;
        // Price
        const price = (document.querySelector('.price') || {}).textContent;
        let nights;
        if (widgetData.dateBegin && widgetData.dateEnd) {
            nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
        }
        if (price && nights) {
            widgetData.price = thn.parseNumber(price) / nights;
            const cur = price.match(/[^\d\s,.]+/);
            // Currency
            if (cur) {
                widgetData.currency = thn.getCurrencyCode(cur[0]);
            }
            widgetData.set = true;
        }
        else {
            widgetData.set = false;
            // Check if there are no rooms, to have no-rooms we need to satisfy the following rules:
            // - message_container container exists "We're sorry but there are no avaiable..."
            // - availability_calendar_container exists (element where se see the availability of each month)
            // - availability_list (room wrapper) exists and doesn't have children
            const loading = document.querySelector('.spinner_container .spinner');
            const noRooms = document.querySelector('.message_container')
                && document.querySelector('.availability_calendar_container')
                && (document.querySelector('.availability_list') && !document.querySelector('.availability_list > div'));
            if (noRooms && !loading) {
                widgetData.noRooms = true;
            }
        }
        return widgetData;
    };
    /**
         * @memberOf module:bs_234
         * @this thn.widgets.booking
         * @see {@link Booking#set_data}
         *
         * @param {object} ctx - The context object.
         */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        const data = window.createdBooking;
        if (data && !window.location.pathname.match('/reservation/confirmation')) {
            // Parsing of the createdBooking object is done in an external fn
            // because It is used here and also in the corpo engine
            const context = { ...ctx, thn };
            const results = getBookingWidgetDataFromObject(data, context);
            Object.assign(widgetData, results);
        }
        else { // DOM alternative
            const bookingEl = document.querySelector('.reserve_number');
            if (bookingEl) {
                widgetData.bookingId = bookingEl.textContent;
            }
            // Dates
            const dateBeginEl = document.querySelector('.checkin_column');
            if (dateBeginEl) {
                widgetData.dateBegin = dateBeginEl.textContent.replace(/(\d{2})\D+(\d{2})\D+(\d{4})/, '$3/$2/$1');
            }
            const dateEndEl = document.querySelector('.checkout_column');
            if (dateEndEl) {
                widgetData.dateEnd = dateEndEl.textContent.replace(/(\d{2})\D+(\d{2})\D+(\d{4})/, '$3/$2/$1');
            }
            const priceEls = document.querySelectorAll('.reserve_total .grand_total');
            if (priceEls && priceEls.length) {
                const priceEl = priceEls[priceEls.length - 1];
                // Currency
                widgetData.currency = thn.getCurrencyCode((priceEl.textContent.match(/[^\d\s,.]+/) || [])[0]);
                // Price
                widgetData.price = thn.parseNumber(priceEl.textContent.trim());
            }
        }
        return widgetData;
    };
    engine.getPromocode = function getPromocode(ctx) {
        const hashPromocode = thn.parseUrlParameter('promo', window.location.hash);
        if (hashPromocode) {
            ctx.promocode = hashPromocode;
        }
        return ctx.promocode;
    };
    return engine;
}

/**
 * Returns true when the booking engine is v3.
 *
 * It is hard to know the engine verion before the DOM is loaded so we try
 * to rely on a variable that is loaded with the HTML.
 */
function checkV3() {
    if (window.bookingVersion) {
        return false;
    }
    return true;
}
/**
 * **Bedclouds**
 *
 * @exports bs_234
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = v1v2Factory({ window, document, thn });
    thn.logger.debug('--- Bedclouds v1v2 ---');
    if (checkV3()) {
        thn.logger.debug('--- Bedclouds v3 ---');
        const engine3 = v3Factory({ window, document, thn });
        Object.assign(engine, engine3);
    }
    return engine;
}

export { factory };
